import { UI } from '@genial/components';
import { ColorTypes } from '@genial/design-tokens';
import React from 'react';

import { useRecoilQuery } from '../../../../hooks/useRecoilQuery';
import { useManagementAnalysisActions } from '../../../../store/managementAnalysis/actions';
import { selectedConversionYearState } from '../../../../store/managementAnalysis/atom';
import { conversionDataQuery } from '../../../../store/managementAnalysis/selector';
import { mountChartMobileLegend } from '../../constants';

export declare namespace ConversionChartProps {
  interface Props {
    yearsLoading: boolean;
    yearsError?: boolean;
    years?: string[];
    isMobile?: boolean;
  }
}

interface ConversionItemsProps {
  color: ColorTypes.Option;
  label: string;
  mobileLabel: string;
  dataKey: string;
}

const conversionBars: ConversionItemsProps[] = [
  {
    color: 'brand.secondary.dark',
    label: 'HABILITAÇÃO',
    mobileLabel: 'HAB',
    dataKey: 'monthlyEnabledAccounts',
  },
  {
    color: 'brand.secondary.principal',
    label: 'ATIVAÇÃO',
    mobileLabel: 'ATV',
    dataKey: 'monthlyActivatedAccounts',
  },
];
const conversionLine: ConversionItemsProps = {
  color: 'neutral.light',
  label: '%CONVERSÃO',
  mobileLabel: '%CONV',
  dataKey: 'monthlyConversion',
};

export const ConversionChart: React.FC<ConversionChartProps.Props> = ({
  years,
  yearsLoading,
  yearsError,
  isMobile,
}) => {
  const { setSelectedConversionYear } = useManagementAnalysisActions();
  const { data: yearSelected } = useRecoilQuery(selectedConversionYearState);
  const {
    loading: dataLoading,
    error: dataError,
    data,
    refresher,
  } = useRecoilQuery(conversionDataQuery);

  const getConversionItems = (forceLabel = false) => {
    const conversionItems = [...conversionBars, conversionLine];
    if (!isMobile || forceLabel) {
      return conversionItems;
    }
    return conversionItems.map((item) => ({
      ...item,
      label: item.mobileLabel,
    }));
  };

  const loading = yearsLoading || dataLoading;
  const hasError = yearsError || dataError;

  return (
    <UI.Chart
      isMobile={isMobile}
      error={!!hasError}
      loading={loading}
      chartTitle="CONVERSÃO"
      mobileLegend={mountChartMobileLegend(data?.conversion)}
      legendInfo={[
        { title: 'TOTAL', value: data?.information?.periodConversionFormatted ?? '--' },
        { title: 'MEDIA', value: data?.information?.averageConversionFormatted ?? '--' },
        { title: 'MTD', value: data?.information?.currentMonthConversionFormatted ?? '--' },
      ]}
      legendItems={getConversionItems()}
      onReloadChart={() => refresher?.()}
      onYearChange={setSelectedConversionYear}
      yearOptions={years}
      yearSelected={yearSelected}
      chartComponent={
        <UI.ConversionChart
          isMobile={isMobile}
          data={data?.conversion}
          additionalData={{
            year: parseInt(yearSelected, 10),
            tooltipLabels: getConversionItems(true),
          }}
          bars={conversionBars}
          line={conversionLine}
        />
      }
    />
  );
};
